import type { JSX } from 'react'

import Autocomplete from '@mui/material/Autocomplete'
import TextField from '@mui/material/TextField'

import type { Styles } from '../../theme/types/Styles'

type CommonProps<T extends Record<string, any>> = {
  label?: string
  'aria-label'?: string
  options: T[] | Readonly<T[]>
  getOptionLabel?: (option: T) => string
  isOptionEqualToValue?: (option: T, value: T) => boolean
  groupBy?: (option: T) => string
  error?: boolean
  helperText?: string | null
  required?: boolean
  disabled?: boolean
  fullWidth?: boolean
  size?: 'small' | 'medium'
  variant?: 'outlined' | 'standard' | 'filled'
  /**
   * Zoekt server-side op getypte invoer; de opties worden dan getoond zoals
   * ze binnenkomen, zonder client-side filtering.
   */
  onSearch?: (text: string) => void
  loading?: boolean
  noOptionsText?: string
  sx?: Styles
}

type SingleClearableProps<T extends Record<string, any>> = CommonProps<T> & {
  multiple?: false
  disableClearable?: false
  value: T | null
  onChange: (value: T | null) => void
}

type SingleNonClearableProps<T extends Record<string, any>> = CommonProps<T> & {
  multiple?: false
  disableClearable: true
  value: T
  onChange: (value: T) => void
}

type MultipleClearableProps<T extends Record<string, any>> = CommonProps<T> & {
  multiple: true
  disableClearable?: false
  value: T[] | null
  onChange: (value: T[]) => void
}

type MultipleNonClearableProps<T extends Record<string, any>> =
  CommonProps<T> & {
    multiple: true
    disableClearable: true
    value: T[]
    onChange: (value: T[]) => void
  }

export function SelectObject<
  T extends Record<string, any> = Record<string, any>,
>(props: SingleClearableProps<T>): JSX.Element
export function SelectObject<
  T extends Record<string, any> = Record<string, any>,
>(props: SingleNonClearableProps<T>): JSX.Element
export function SelectObject<
  T extends Record<string, any> = Record<string, any>,
>(props: MultipleClearableProps<T>): JSX.Element
export function SelectObject<
  T extends Record<string, any> = Record<string, any>,
>(props: MultipleNonClearableProps<T>): JSX.Element
export function SelectObject<
  T extends Record<string, any> = Record<string, any>,
>({
  label,
  'aria-label': ariaLabel,
  value,
  options,
  onChange,
  getOptionLabel,
  isOptionEqualToValue,
  groupBy,
  error,
  helperText,
  required = false,
  disabled = false,
  disableClearable = false,
  fullWidth = false,
  size = 'small',
  variant = 'standard',
  onSearch,
  loading = false,
  noOptionsText,
  sx,
  multiple = false,
}:
  | SingleClearableProps<T>
  | SingleNonClearableProps<T>
  | MultipleClearableProps<T>
  | MultipleNonClearableProps<T>) {
  const getDefaultLabel = (option: T): any => {
    if ('label' in option && typeof (option as any).label === 'string') {
      return (option as T & { label: string }).label
    }

    return undefined
  }

  const getDefaultGroup = (option: T): any => {
    if ('group' in option && typeof (option as any).group === 'string') {
      return (option as T & { group: string }).group
    }

    return undefined
  }

  return (
    <Autocomplete
      value={value ?? (multiple ? [] : null)}
      options={options}
      getOptionLabel={getOptionLabel || getDefaultLabel}
      groupBy={groupBy || getDefaultGroup}
      multiple={multiple}
      isOptionEqualToValue={isOptionEqualToValue}
      filterSelectedOptions
      filterOptions={onSearch ? (options) => options : undefined}
      onInputChange={
        onSearch
          ? (_, text, reason) => {
              if (reason === 'input') onSearch(text)
            }
          : undefined
      }
      loading={loading}
      loadingText="Zoeken…"
      noOptionsText={noOptionsText}
      disabled={disabled}
      onChange={(_, newValue) => {
        onChange(newValue as any)
      }}
      disableClearable={disableClearable}
      fullWidth={fullWidth}
      size={size}
      renderInput={(params) => (
        <TextField
          {...params}
          fullWidth
          required={required}
          disabled={disabled}
          variant={variant}
          label={label}
          aria-label={ariaLabel}
          error={error}
          helperText={helperText}
        />
      )}
      sx={sx}
    />
  )
}
